import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '../../enum/PostHogEvent.enum';
import errorMapper, { ErrorCodes, type Error, type ErrorType } from '../../utils/errorMapper';
import { useCaptchaFallback } from './useCaptchaFallback.hook';
import { QueryKey } from '@/Config/api/queryKey';
import { capturePostHogSafely, getPostHogFeatureFlagSafely } from '@/Domain/helper/posthogCapture';
import { PASSWORD_MIN_LENGTH } from '@/Domain/utils/passwordPolicy';
import { setPendingVerificationEmail } from '@/Domain/utils/pendingVerificationEmail';

export const EMAIL_TAKEN_ERROR_TYPE = 'email_taken';

const DEFAULT_FORM_VALUES = {
  username: '',
  password1: '',
  password2: '',
  tos_consent: true,
  pp_consent: true,
  recaptcha_response_v3: '',
};

const createRegisterValidator = () =>
  z
    .object({
      username: z.string().email(i18n.t('validation:invalid_email')),
      password1: z
        .string()
        .min(
          PASSWORD_MIN_LENGTH,
          i18n.t('validation:password_min_length', { min: PASSWORD_MIN_LENGTH }),
        ),
      password2: z.string().min(1, i18n.t('validation:password_required')),
      tos_consent: z.boolean(),
      pp_consent: z.boolean(),
      recaptcha_response_v3: z.string().optional(),
      recaptcha_response_v2: z.string().optional(),
    })
    .refine((data) => data.password1 === data.password2, {
      message: i18n.t('validation:match_password'),
      path: ['password2'],
    });

export type RegisterForm = z.infer<ReturnType<typeof createRegisterValidator>>;

type RegisterStatus =
  | 'succeeded'
  | 'validation_failed'
  | 'captcha_required'
  | 'rejected'
  | 'rate_limited'
  | 'server_error';

const isRegisterErrorType = (value: unknown): value is ErrorType<RegisterForm> =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.values(value).every(
    (errors) =>
      Array.isArray(errors) &&
      errors.every(
        (error) =>
          error !== null &&
          typeof error === 'object' &&
          typeof (error as { message?: unknown }).message === 'string',
      ),
  );

export const useRegister = () => {
  useTranslation('validation');
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const attemptStartedAt = useRef<number | null>(null);
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    mode: 'onTouched',
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(createRegisterValidator()),
  });

  const captureAttempt = useCallback(
    (captchaMode: 'v2' | 'v3') => {
      attemptStartedAt.current = Date.now();
      capturePostHogSafely(postHog, PostHogEventEnum.RegisterAttempt, {
        method: 'email',
        captcha_mode: captchaMode,
      });
    },
    [postHog],
  );

  const captureResult = useCallback(
    (status: RegisterStatus, props: Record<string, unknown> = {}) => {
      const startedAt = attemptStartedAt.current;
      attemptStartedAt.current = null;
      capturePostHogSafely(postHog, PostHogEventEnum.RegisterResult, {
        method: 'email',
        status,
        duration_ms: startedAt === null ? null : Date.now() - startedAt,
        ...props,
      });
    },
    [postHog],
  );

  const {
    handleCaptchaError,
    handleSubmissionError,
    escalateToV2,
    handleV2Focus,
    isPreparingSubmission,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('register:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const mutation = useMutation<{ email: string }, Error<RegisterForm>, RegisterForm>({
    mutationKey: [QueryKey.accounts.register],
    onSuccess: (response) => {
      const { email } = response;

      // Get the A/B test variant for tracking
      const backgroundVariant = getPostHogFeatureFlagSafely(
        postHog,
        PostHogFeatureFlagEnum.SignupBackgroundTest,
      );

      // Capture in PostHog for experiment analysis (before navigation)
      capturePostHogSafely(postHog, PostHogEventEnum.SignupCompleted, {
        background_variant: backgroundVariant,
      });

      track('register_form_success', {
        event_category: 'User Registration',
        event_label: 'register_form',
        background_variant: backgroundVariant ?? 'unknown',
      });

      captureResult('succeeded', { background_variant: backgroundVariant ?? 'unknown' });

      setPendingVerificationEmail(email);

      // `account_created` GTM event fires from main.tsx on the `?account_activated=1` redirect after email verification.
      // Keep the query (e.g. a marketplace-install `next`) so a resend from the verify page re-carries it.
      navigate(
        { pathname: '/register/verify', search: window.location.search },
        { viewTransition: true },
      );
    },
    onError: (error) => {
      handleSubmissionError();
      track('register_form_error', {
        event_category: 'User Registration',
        event_label: 'register_form',
      });

      const httpStatus = error.response?.status;
      const httpStatusProperties = httpStatus === undefined ? {} : { http_status: httpStatus };
      const fieldErrors = error.response?.data?.errors;
      const hasMappableFieldErrors = isRegisterErrorType(fieldErrors);

      if (
        !error.response ||
        httpStatus === undefined ||
        httpStatus < 400 ||
        httpStatus >= 500 ||
        (![401, 429].includes(httpStatus) && !hasMappableFieldErrors)
      ) {
        const message = i18n.t('register:error_generic');
        setError('root', { message });
        toast.error(message);
        captureResult('server_error', httpStatusProperties);
        return;
      }

      let bannerMessage = httpStatus === 429 ? i18n.t('ERRORS:TOO_MANY_REQUESTS') : undefined;
      let handled = false;
      const rejectedFields = new Set<string>();
      const errorCodes = new Set<string>();
      let captchaEscalated = httpStatus === 401;
      let rateLimited = httpStatus === 429;

      if (captchaEscalated) {
        handleCaptchaError('recaptcha_response_v3');
        handled = true;
      }

      if (hasMappableFieldErrors) {
        errorMapper(fieldErrors, (field, message, code) => {
          rejectedFields.add(String(field));
          if (code) {
            errorCodes.add(String(code));
          }
          if (handleCaptchaError(field)) {
            captchaEscalated = true;
            if (field === 'recaptcha_response_v2') {
              toast.error(i18n.t('register:error_generic'));
            }
            handled = true;
            return;
          }
          if (code === ErrorCodes.TOO_MANY_REQUESTS) {
            rateLimited = true;
            bannerMessage = i18n.t('ERRORS:TOO_MANY_REQUESTS');
            return;
          }
          if (field === 'username' && code === ErrorCodes.UNIQUE) {
            setError('username', {
              type: EMAIL_TAKEN_ERROR_TYPE,
              message: i18n.t('register:error_email_taken'),
            });
            handled = true;
            return;
          }
          if (field === 'username' || field === 'password1' || field === 'password2') {
            setError(field, { message });
            handled = true;
            return;
          }
          bannerMessage = message || bannerMessage;
        });
      }

      const status: RegisterStatus = rateLimited
        ? 'rate_limited'
        : captchaEscalated
          ? 'captcha_required'
          : 'rejected';
      captureResult(status, {
        ...httpStatusProperties,
        rejected_fields: Array.from(rejectedFields),
        error_codes: Array.from(errorCodes),
      });

      if (!bannerMessage && !handled) {
        bannerMessage = i18n.t('register:error_generic');
      }

      if (bannerMessage) {
        setError('root', { message: bannerMessage });
        toast.error(bannerMessage);
      }
    },
  });

  const onSubmit = async (data: RegisterForm) => {
    captureAttempt(useV2 ? 'v2' : 'v3');

    const payload = await prepareCaptchaSubmission(data);
    if (!payload) {
      captureResult('captcha_required', { blocked_before_request: true });
      return;
    }

    track('register_form_submit', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

    mutation.mutate(payload);
  };

  const onInvalidSubmit = (formErrors: FieldErrors<RegisterForm>) => {
    captureAttempt(useV2 ? 'v2' : 'v3');
    captureResult('validation_failed', {
      invalid_fields: Object.keys(formErrors),
      blocked_before_request: true,
    });
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [setValue],
  );

  const captchaFallbackReported = useRef(false);
  useEffect(() => {
    if (!useV2 || captchaFallbackReported.current) {
      return;
    }
    captchaFallbackReported.current = true;
    capturePostHogSafely(postHog, PostHogEventEnum.RegisterCaptchaFallbackShown, {
      method: 'email',
    });
  }, [useV2, postHog]);

  register('tos_consent');
  register('pp_consent');
  register('recaptcha_response_v3');

  return {
    control,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    onInvalidSubmit,
    errors,
    onVerify,
    watch,
    isPending: mutation.isPending || isPreparingSubmission,
  };
};
